// Dashboard (PX-450) — product snapshot: KPI cards + trend charts over the
// real aggregate endpoint GET /admin/dashboard/stats (gated audit.read).
//
// Monetization is count-based ONLY — purchase_events stores no money amount,
// so there is no dollar-revenue figure here (conversion + purchase counts).
// Operators without audit.read (content / support / billing zones) get a 403
// and see a graceful locked state instead of an error.

import { useQuery } from '@tanstack/react-query'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { APIError, request } from '@/lib/api'
import type { AdminDashboardStats } from '@/types/api'

// Chart colours — match the admin Tailwind palette (recharts needs raw SVG
// fills, so these mirror tailwind.config brand/ink + the status tones used
// across the app).
const C = {
  completed: '#10b981', // emerald-500
  failed: '#ef4444', // red-500
  processing: '#f59e0b', // amber-500
  queued: '#8b91a8', // ink-300
  brand: '#ec4f95', // brand-500
  grid: '#e8eaf1', // ink-100
  axis: '#5b6178', // ink-400
}

const STATUS_COLOR: Record<string, string> = {
  completed: C.completed,
  failed: C.failed,
  processing: C.processing,
  queued: C.queued,
}

const pct = (v: number) => `${(v * 100).toFixed(1)}%`
// "2026-06-15" → "15.06" for compact axis ticks.
const shortDate = (iso: string) => {
  const [, m, d] = iso.split('-')
  return `${d}.${m}`
}

function StatCard({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <div className="rounded-xl border border-ink-100 bg-white p-4 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-ink-400">{label}</div>
      <div className="mt-1 text-2xl font-semibold tracking-tight text-ink-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-ink-400">{hint}</div>}
    </div>
  )
}

function ChartCard({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
      <h2 className="text-base font-semibold tracking-tight text-ink-900">{title}</h2>
      {subtitle && <p className="mb-2 mt-0.5 text-xs text-ink-400">{subtitle}</p>}
      <div className="mt-3 h-64">{children}</div>
    </div>
  )
}

export function DashboardPage() {
  const query = useQuery({
    queryKey: ['admin', 'dashboard', 'stats'],
    queryFn: () => request<AdminDashboardStats>('/admin/dashboard/stats'),
    retry: false, // a 403 (no audit.read) shouldn't retry — show the locked state
  })

  const forbidden = query.error instanceof APIError && query.error.status === 403

  return (
    <div className="p-6">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <p className="text-sm text-ink-400">Product snapshot — users, generations, subscriptions, monetization.</p>
      </header>

      {forbidden && (
        <div className="rounded-xl border border-ink-100 bg-white p-8 text-center shadow-sm">
          <div className="text-sm font-medium text-ink-700">Metrics require the “audit.read” permission</div>
          <div className="mt-1 text-xs text-ink-400">Your role (content / support / billing zone) doesn’t include dashboard analytics. Ask an admin if you need access.</div>
        </div>
      )}

      {!forbidden && query.error instanceof APIError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {query.error.message}
        </div>
      )}

      {query.isLoading && <div className="text-sm text-ink-400">Loading…</div>}

      {query.data && <DashboardBody stats={query.data} />}
    </div>
  )
}

function DashboardBody({ stats }: { stats: AdminDashboardStats }) {
  const { users, subscriptions, generations, monetization, generations_daily, new_users_daily } = stats

  // Donut data — drop zero slices so the chart isn't cluttered with empties.
  const statusData = Object.entries(generations.by_status)
    .filter(([, v]) => v > 0)
    .map(([name, value]) => ({ name, value }))

  return (
    <div className="flex flex-col gap-6">
      {/* KPI cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Users" value={users.total} hint={`+${users.new_today} today · +${users.new_7d} 7d`} />
        <StatCard label="Active subs" value={subscriptions.active_paid} hint="paid, not expired" />
        <StatCard label="Generations" value={generations.total} hint={`${generations.today} today`} />
        <StatCard label="Success rate" value={pct(generations.success_rate_30d)} hint="completed / terminal, 30d" />
        <StatCard label="Conversion" value={pct(monetization.conversion_pct)} hint={`${monetization.paying_users} paying`} />
        <StatCard label="Purchases" value={monetization.purchases_30d} hint="paid events, 30d" />
      </div>

      {/* Generations per day */}
      <ChartCard title="Generations / day" subtitle="Last 30 days · completed vs failed">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={generations_daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
            <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: C.axis }} interval="preserveStartEnd" minTickGap={24} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.axis }} width={36} />
            <Tooltip labelFormatter={(label) => shortDate(String(label))} contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: C.grid }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="completed" stackId="1" stroke={C.completed} fill={C.completed} fillOpacity={0.25} />
            <Area type="monotone" dataKey="failed" stackId="1" stroke={C.failed} fill={C.failed} fillOpacity={0.25} />
          </AreaChart>
        </ResponsiveContainer>
      </ChartCard>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* New users per day */}
        <ChartCard title="New users / day" subtitle="Last 30 days">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={new_users_daily} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.grid} vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 11, fill: C.axis }} interval="preserveStartEnd" minTickGap={24} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: C.axis }} width={36} />
              <Tooltip labelFormatter={(label) => shortDate(String(label))} contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: C.grid }} />
              <Bar dataKey="count" fill={C.brand} radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        {/* Generation status breakdown */}
        <ChartCard title="Generation status" subtitle="All-time breakdown">
          {statusData.length === 0 ? (
            <div className="flex h-full items-center justify-center text-sm text-ink-400">No generations yet.</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={statusData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                  {statusData.map((s) => (
                    <Cell key={s.name} fill={STATUS_COLOR[s.name] ?? C.queued} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: C.grid }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* By-plan breakdown (compact) */}
      {subscriptions.by_plan.length > 0 && (
        <div className="rounded-xl border border-ink-100 bg-white p-5 shadow-sm">
          <h2 className="text-base font-semibold tracking-tight text-ink-900">Active subscriptions by plan</h2>
          <div className="mt-3 flex flex-wrap gap-2">
            {subscriptions.by_plan.map((p) => (
              <span key={p.plan_name} className="inline-flex items-center gap-1.5 rounded-md bg-ink-50 px-2.5 py-1 text-sm">
                <span className="font-medium text-ink-800">{p.plan_name}</span>
                <span className="tabular-nums text-ink-500">{p.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
